// src/receptionist/screens/Conversation.tsx
//
// The whole conversation design in one screen, split into the three surfaces
// that WhatsApp's own rules force apart (see the FollowUp doc comment in
// ../flow):
//
//   Opener      first contact, inside the 24h window, free text
//   Steps       the capture conversation, inside the window, free text
//   Follow-ups  scheduled, outside the window, approved templates only
//
// Wired to the flow API (ourteam-frontend#3608): the flow arrives as a prop,
// Save goes through the server, and the server's validation problems render
// in the same list as the local ones — the merchant should not have to care
// which side found a problem.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ListChecksIcon, MessageCircleIcon, SendIcon } from "lucide-react";
import { PageHeader, Button } from "../components/kit";
import { PageSectionTabs } from "../components/ui";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@component/select";
import BookingFlowBuilder from "../components/BookingFlowBuilder";
import { OpenerEditor } from "../components/OpenerEditor";
import { FollowUpEditor } from "../components/FollowUpEditor";
import { validateFlow, flowWarnings, needsTemplate, templateKeyFor, type BookingFlow as Flow, type FollowUp, type LiveTemplateStatus } from "../flow";
import { AlertTriangleIcon, XCircleIcon } from "lucide-react";
import { PillLink, TYPE, type Tone } from "@oura/ui";
import { cn } from "@oura/lib/cn";
import { TagPill } from "../components/ui";
import type { FlowScreenProps } from "../flowState";
import { getFlowTemplates, getWhatsappState, type FlowTemplate } from "@service/receptionist";

type Surface = "opener" | "steps" | "followups";

interface Props extends FlowScreenProps {
    /** Bookable service names, from the loaded merchant config. */
    services?: string[];
    /** For the templates' real status (GET .../whatsapp). */
    merchantId?: string | null;
    /** Setup → Message templates, where a template is submitted. */
    templatesHref?: string;
}

const LIVE_TAG: Record<LiveTemplateStatus, { label: string; tone: Tone }> = {
    approved: { label: "Approved", tone: "green" },
    pending: { label: "Awaiting Meta", tone: "amber" },
    not_submitted: { label: "Not submitted", tone: "gray" },
    rejected: { label: "Rejected", tone: "red" },
};

export function ConversationScreen({ flow: server, onSave, services = [], merchantId, templatesHref }: Props) {
    // The templates' real status, so a follow-up never reads "not submitted" when Meta has
    // already rejected or approved its template (29 Oct 2026). The flow's own copy is the fallback.
    // Scoped to the merchant it was read for (Jack, #4804): another merchant's statuses are never used.
    const [live, setLive] = useState<{ merchantId: string; statuses: Record<string, LiveTemplateStatus> } | null>(null);
    useEffect(() => {
        setLive(null);
        if (!merchantId) return;
        let on = true;
        getWhatsappState(merchantId)
            .then((s) => {
                if (on) setLive({ merchantId, statuses: Object.fromEntries(s.templates.map((t) => [t.key, t.status as LiveTemplateStatus])) });
            })
            .catch(() => undefined);
        return () => { on = false; };
    }, [merchantId]);
    const statusOf = useCallback(
        (f: FollowUp): LiveTemplateStatus => {
            const key = templateKeyFor(f);
            const statuses = live && live.merchantId === merchantId ? live.statuses : null;
            return (key && statuses?.[key]) || f.templateStatus;
        },
        [live, merchantId],
    );
    // The working draft, replaced wholesale whenever the server's copy changes
    // (a save that returned the new version). Local edits never survive that.
    const [flow, setFlow] = useState<Flow>(server);
    const [dirty, setDirty] = useState(false);
    useEffect(() => { setFlow(server); setDirty(false); setServerProblems([]); }, [server]);

    // ?tab=followups (or opener) opens that part, so Setup → Message templates can link straight to the wording.
    const [params] = useSearchParams();
    const asked = params.get("tab");
    const [surface, setSurface] = useState<Surface>(asked === "followups" || asked === "opener" || asked === "steps" ? (asked as Surface) : "steps");
    const [saving, setSaving] = useState(false);
    /**
     * Problems the SERVER reported on the last save. Kept apart from the local
     * list only so they can be cleared on the next edit; on screen they are
     * one list, styled identically.
     */
    const [serverProblems, setServerProblems] = useState<string[]>([]);
    const localErrors = useMemo(() => validateFlow(flow), [flow]);
    /**
     * Send-time conditions. They are shown next to the errors but never block
     * the save — see `flowWarnings`.
     */
    // Follow-ups that cannot go out yet: on, outside the 24-hour window, template not approved.
    const blocked = useMemo(
        () => flow.followUps.filter((f) => f.enabled && needsTemplate(f) && statusOf(f) !== "approved"),
        [flow.followUps, statusOf],
    );
    // What stops the save: the flow's own rules, then whatever the server said.
    const errors = useMemo(
        () => [...localErrors, ...serverProblems.filter((p) => !localErrors.includes(p) && !flowWarnings(flow).includes(p))],
        [localErrors, serverProblems, flow],
    );

    /**
     * The starter flows, from the API. `null` until the call answers; an
     * empty list means it failed, and then the picker is not shown at all —
     * an empty picker invites a question the screen cannot answer (the same
     * `templatesAvailable` rule as the hiring builder).
     */
    const [templates, setTemplates] = useState<FlowTemplate[] | null>(null);
    useEffect(() => {
        let cancelled = false;
        getFlowTemplates()
            .then((list) => { if (!cancelled) setTemplates(list); })
            .catch(() => { if (!cancelled) setTemplates([]); });
        return () => { cancelled = true; };
    }, []);

    const edit = (next: Flow) => { setFlow(next); setDirty(true); setServerProblems([]); };

    const applyTemplate = (key: string) => {
        const tpl = templates?.find((t) => t.key === key);
        if (!tpl) return;
        // The template's steps and follow-ups, on THIS flow's version and
        // opener wording: a starter replaces the conversation, not the
        // merchant's own greeting or the document's history.
        //
        // `template` is set from the key we were given rather than trusted from
        // the template's own payload, so the flow's provenance always names the
        // thing the merchant actually picked — which is what the picker reads
        // back to show its selection.
        edit({ ...tpl.flow, template: tpl.key, version: flow.version, opener: flow.opener });
    };

    // Reload and tab-close carry the browser's own warning while there are
    // unsaved edits. In-app navigation is not intercepted: this app renders a
    // plain <Routes> tree, which has no blocker API, and hijacking the rail's
    // links would be a shared-shell change this product does not make.
    useEffect(() => {
        if (!dirty) return;
        const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [dirty]);

    const save = async () => {
        if (localErrors.length) return;
        setSaving(true);
        try {
            // The server increments `version`; sending the current one lets it
            // tell a stale write from a fresh one.
            const outcome = await onSave(flow);
            if (outcome.ok) setDirty(false);
            else setServerProblems(outcome.problems);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <PageHeader
                title="Conversation"
                subtitle={`What the AI opens with, what it asks before a request reaches your team, and what it sends afterwards. Version ${flow.version}${dirty ? ", with unsaved changes" : ""}.`}
                action={
                    <div className="flex items-center gap-2">
                        {/* The picker is bound to the flow's own provenance, not
                            to "". Hardcoding the empty string sent the trigger
                            back to the placeholder the instant you picked
                            something, so a template that HAD been applied read
                            as one that had not. `flow.template` is the real
                            answer, and a flow that started from no template
                            ("ourteam_default", or anything not in this list)
                            falls through to the placeholder on its own. */}
                        {templates && templates.length > 0 && (
                            <Select value={flow.template} onValueChange={(v) => { if (v) applyTemplate(v); }}>
                                <SelectTrigger
                                    aria-label="Start from a template"
                                    // An Oura pill, as the scan picker beside a page title (#4785).
                                    className="h-8 w-56 rounded-full border-0 bg-white px-4 text-[13px] font-medium text-gray-900 shadow-none ring-1 ring-inset ring-black/[0.08] hover:bg-gray-50 focus:ring-2 focus:ring-blue-600 focus:ring-offset-0"
                                >
                                    <SelectValue placeholder="Start from a template…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {templates.map((tpl) => (
                                        <SelectItem key={tpl.key} value={tpl.key} description={tpl.description}>{tpl.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        <Button onClick={() => void save()} disabled={saving || !dirty || localErrors.length > 0}>
                            {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                }
            />

            {/* The page's sections, in the same tabs as Settings (#4785). */}
            <PageSectionTabs<Surface>
                value={surface}
                onChange={setSurface}
                options={[
                    { value: "opener", label: "Opener", icon: MessageCircleIcon },
                    { value: "steps", label: "Booking steps", count: flow.steps.length, icon: ListChecksIcon },
                    { value: "followups", label: "Follow-ups", count: flow.followUps.filter((f) => f.enabled).length, icon: SendIcon },
                ]}
            />

            {errors.length > 0 && (
                <div role="alert" className="mb-5 rounded-xl bg-rose-50 px-4 py-3 ring-1 ring-inset ring-rose-200">
                    <p className={cn(TYPE.tc, "m-0 flex items-center gap-2 font-semibold text-rose-800")}>
                        <XCircleIcon aria-hidden className="h-4 w-4 shrink-0" /> Fix {errors.length === 1 ? "this" : "these"} before saving
                    </p>
                    <ul className={cn(TYPE.tc, "m-0 mt-1.5 list-disc space-y-0.5 pl-10 text-rose-800")}>
                        {errors.map((e) => <li key={e}>{e}</li>)}
                    </ul>
                </div>
            )}

            {/* One notice for the follow-ups that cannot go out yet, by name, with where to fix it. */}
            {blocked.length > 0 && (
                <div className="mb-5 rounded-xl bg-amber-50 px-4 py-3.5 ring-1 ring-inset ring-amber-200">
                    <div className="flex flex-wrap items-start gap-3">
                        <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                        <div className="min-w-0 flex-1">
                            <p className={cn(TYPE.tc, "m-0 font-semibold text-amber-900")}>
                                {blocked.length} {blocked.length === 1 ? "follow-up can't" : "follow-ups can't"} send yet
                            </p>
                            <p className={cn(TYPE.tc, "m-0 mt-0.5 text-amber-800")}>
                                They go out after WhatsApp's 24-hour window, so each needs a template Meta has approved.
                            </p>
                            <ul className="list-plain m-0 mt-2 flex flex-wrap gap-1.5 p-0">
                                {blocked.map((f) => (
                                    <li key={f.id} className="inline-flex items-center gap-1.5 rounded-full bg-white py-0.5 pl-3 pr-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-amber-200">
                                        {f.label}
                                        <TagPill tone={LIVE_TAG[statusOf(f)].tone}>{LIVE_TAG[statusOf(f)].label}</TagPill>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        {templatesHref && <PillLink to={templatesHref} size="sm">Open Message templates</PillLink>}
                    </div>
                </div>
            )}

            {surface === "opener" && (
                <OpenerEditor opener={flow.opener} onChange={(opener) => edit({ ...flow, opener })} />
            )}
            {surface === "steps" && (
                <BookingFlowBuilder flow={flow} onChange={edit} services={services} />
            )}
            {surface === "followups" && (
                <FollowUpEditor followUps={flow.followUps} onChange={(followUps) => edit({ ...flow, followUps })} statusOf={statusOf} />
            )}
        </div>
    );
}
